import { useTranslation } from 'react-i18next';
import SVGIcon from '../../../components/atoms/icons';
import CampoAssinaturaDigital from './CampoAssinaturaDigital';
import { problemIcons } from './FormularioAberturaOrdemServico';
import type { FormularioResolucaoOrdemServicoProps } from '../types';

const FormularioResolucaoOrdemServico = ({
	ordemServicoSelecionada,
	containerPainel,
	containerSelecionado,
	formularioResolucao,
	erroResolucaoOrdemServico,
	carregandoResolucaoOrdemServico,
	aoVoltar,
	aoAlterarCampo,
	aoAlterarFotoDepois,
	aoRemoverFotoDepois,
	aoAlterarAssinaturaTecnico,
	aoAlterarAssinaturaGestor,
	aoSalvarResolucao,
	formatarDataHora,
	formatarStatusOrdemServico,
	formatarTemperatura,
	formatarTipoProblema,
}: FormularioResolucaoOrdemServicoProps) => {
	const { t } = useTranslation();
	const ordemEmAtendimento = ordemServicoSelecionada.status === 'em-atendimento';
	const containerOrdemServico = ordemServicoSelecionada.container || containerSelecionado;
	const tiposProblemaNumericos = Array.isArray(ordemServicoSelecionada.tipoProblema)
		? (ordemServicoSelecionada.tipoProblema as number[])
		: [];
	const problemasSelecionados = problemIcons.filter((problema) =>
		tiposProblemaNumericos.includes(problema.id)
	);

	return (
		<section className="mx-auto max-w-5xl px-6 py-6">
			<p className="mb-7 font-mono text-xs text-[#64748b]">
				{t('fluxo_unico.breadcrumb.email')} › {t('fluxo_unico.breadcrumb.record')} ›{' '}
				<span className="text-[#2f70f0]">
					{t('fluxo_unico.breadcrumb.resolve_report', { id: ordemServicoSelecionada.id })}
				</span>
			</p>

			<div className="flex flex-wrap items-center gap-4">
				<button
					aria-label={t('fluxo_unico.form.back_panel')}
					className="flex size-8 items-center justify-center rounded-md border border-[#cbd5e1] bg-white transition-colors hover:border-[#2f70f0] hover:bg-[#f8fafc]"
					onClick={aoVoltar}
					type="button"
				>
					<SVGIcon
						className="h-2 w-3 fill-[#475569]"
						direction="left"
						icon="Arrow"
					/>
				</button>
				<h1 className="text-3xl font-bold tracking-normal">
					{t('fluxo_unico.resolution.title', { id: ordemServicoSelecionada.id })}
				</h1>
				<span
					className={`rounded border px-4 py-2 font-mono text-xs font-bold ${ordemEmAtendimento
						? 'border-[#ef4444] bg-[#fff1f2] text-[#dc2626]'
						: 'border-[#f59e0b] bg-[#fffbeb] text-[#b45309]'
						}`}
				>
					{formatarStatusOrdemServico(ordemServicoSelecionada.status)}
				</span>
			</div>
			<p className="mt-3 text-sm text-[#64748b]">
				{t('fluxo_unico.form.container')}:{' '}
				{containerOrdemServico?.label || containerPainel} - {containerOrdemServico.carrier} | {' '}
				{t('fluxo_unico.resolution.opened_at')} {formatarDataHora(ordemServicoSelecionada.data)}
			</p>

			<div className="mt-8 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] p-6">
				<h2 className="font-semibold">📊 {t('fluxo_unico.resolution.alert_summary')}</h2>
				<div className="mt-6 grid gap-5 sm:grid-cols-4">
					<div>
						<p className="text-xs text-[#64748b]">
							{t('fluxo_unico.resolution.detected_problem')}
						</p>
						<p className="mt-2 flex flex-wrap items-center gap-2 text-[#dc2626]">
							{problemasSelecionados.length ? (
								problemasSelecionados.map((problema) => (
									<span
										aria-label={problema.label}
										className="flex size-12 items-center justify-center rounded-md border border-[#cbd5e1]"
										key={problema.id}
										title={problema.label}
										style={{ backgroundColor: problema.backgroundColor }}
									>
										<SVGIcon
											className="size-8"
											icon={problema.iconeSvg}
											style={{
												fill: problema.iconColor,
												color: problema.iconColor,
												stroke: problema.iconColor,
											}}
										/>
									</span>
								))
							) : (
								<span className="font-mono text-sm font-bold">
									{Array.isArray(ordemServicoSelecionada.tipoProblema)
										? '-'
										: formatarTipoProblema(ordemServicoSelecionada.tipoProblema)}
								</span>
							)}
						</p>
					</div>
					<div>
						<p className="text-xs text-[#64748b]">SetPoint</p>
						<p className="mt-2 font-mono text-sm font-bold">
							{formatarTemperatura(ordemServicoSelecionada.setPoint)}
						</p>
					</div>
					<div>
						<p className="text-xs text-[#64748b]">
							{t('fluxo_unico.form.alert_temperature')}
						</p>
						<p className="mt-2 font-mono text-sm font-bold text-[#dc2626]">
							{formatarTemperatura(
								ordemServicoSelecionada.temperatura
							)}
						</p>
					</div>
					<div>
						<p className="text-xs text-[#64748b]">{t('fluxo_unico.resolution.detection_date')}</p>
						<p className="mt-2 font-mono text-sm font-bold">
							{formatarDataHora(ordemServicoSelecionada.data)}
						</p>
					</div>
				</div>
			</div>

			<form
				className="mt-6"
				onSubmit={(event) => {
					event.preventDefault();
					aoSalvarResolucao('fechada');
				}}
			>
				<div className="rounded-lg border border-[#d9e1ea] bg-white p-7 shadow-sm">
					<h2 className="mb-7 font-semibold">✅ {t('fluxo_unico.resolution.report_resolution')}</h2>
					<div className="space-y-5">
						<div>
							<p className="mb-2 text-sm font-semibold">
								<span className="text-[#dc2626]">*</span> {t('fluxo_unico.resolution.after_photo_required')}
							</p>
							<label className="flex min-h-44 cursor-pointer flex-col items-center justify-center gap-3 rounded-md border border-dashed border-[#cbd5e1] bg-[#f8fafc] transition-colors hover:border-[#2f70f0]">
								<input
									accept="image/*"
									className="hidden"
									multiple
									onChange={aoAlterarFotoDepois}
									type="file"
								/>
								<span className="text-4xl">📸</span>
								<span className="text-sm font-semibold">
									{t('fluxo_unico.resolution.upload_photos')}
								</span>
								<span className="-mt-2 text-xs text-[#64748b]">
									{t('fluxo_unico.resolution.accepted_formats')}
								</span>
							</label>
							{formularioResolucao.imagensAposServico.length > 0 && (
								<div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
									{formularioResolucao.imagensAposServico.map(
										(imagemAposServico, indiceFoto) => (
											<div
												className="relative overflow-hidden rounded-md border border-[#cbd5e1] bg-white"
												key={`${imagemAposServico}-${indiceFoto}`}
											>
												<img
													alt={t('fluxo_unico.resolution.after_photo_alt', { number: indiceFoto + 1 })}
													className="h-32 w-full object-cover"
													src={imagemAposServico}
												/>
														<button
															aria-label={t('fluxo_unico.resolution.remove_after_photo', { number: indiceFoto + 1 })}
															className="absolute right-2 top-2 flex size-7 items-center justify-center rounded-full border border-[#dc2626] bg-[#dc2626] text-sm font-bold leading-none text-white shadow-lg transition-colors hover:bg-[#b91c1c]"
													onClick={() => aoRemoverFotoDepois(indiceFoto)}
													type="button"
												>
													X
												</button>
													<p className="px-3 py-2 font-mono text-xs text-[#64748b]">
													{t('fluxo_unico.form.photo', { number: indiceFoto + 1 })}
												</p>
											</div>
										)
									)}
								</div>
							)}
						</div>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								<span className="text-[#dc2626]">*</span> {t('fluxo_unico.resolution.description_done')}
							</span>
							<textarea
								className="min-h-24 w-full rounded-md border border-[#cbd5e1] bg-white p-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="descricaoServico"
								onChange={aoAlterarCampo}
								placeholder={t('fluxo_unico.resolution.description_placeholder')}
								required
								value={formularioResolucao.descricaoServico}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								{t('fluxo_unico.resolution.local')}
							</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="local"
								onChange={aoAlterarCampo}
								type="text"
								value={formularioResolucao.local}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								{t('fluxo_unico.resolution.setpoint_before')}
							</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-[#f8fafc] px-3 text-[#64748b] disabled:cursor-not-allowed disabled:opacity-80"
								disabled
								value={formatarTemperatura(
									ordemServicoSelecionada.setPoint
								)}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								{t('fluxo_unico.resolution.temperature_before')}
							</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-[#f8fafc] px-3 text-[#64748b] disabled:cursor-not-allowed disabled:opacity-80"
								disabled
								value={formatarTemperatura(
									ordemServicoSelecionada.temperatura
								)}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								{t('fluxo_unico.resolution.set_point_after')}
							</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="setPointAposServico"
								onChange={aoAlterarCampo}
								placeholder="Ex: -5"
								type="number"
								value={formularioResolucao.setPointAposServico}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								<span className="text-[#dc2626]">*</span> {t('fluxo_unico.resolution.temperature_after')}</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="temperaturaAposServico"
								onChange={aoAlterarCampo}
								placeholder="Ex: 10"
								required
								type="number"
								value={formularioResolucao.temperaturaAposServico}
							/>
						</label>

						<div className="grid gap-5 md:grid-cols-2">
							<label className="block text-sm font-semibold">
								<span className="mb-2 block">
									{t('fluxo_unico.resolution.requested_attendency_by')}
								</span>
								<input
									className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
									name="solicitadoPor"
									onChange={aoAlterarCampo}
									type="text"
									value={formularioResolucao.solicitadoPor}
								/>
							</label>

							<label className="block text-sm font-semibold">
								<span className="mb-2 block">
									{t('fluxo_unico.resolution.request_date')}
								</span>
								<input
									className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
									name="dataSolicitacao"
									onChange={aoAlterarCampo}
									type="datetime-local"
									value={formularioResolucao.dataSolicitacao}
								/>
							</label>

							<label className="block text-sm font-semibold">
								<span className="mb-2 block">
									{t('fluxo_unico.resolution.repair_start')}
								</span>
								<input
									className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
									name="inicioReparo"
									onChange={aoAlterarCampo}
									type="datetime-local"
									value={formularioResolucao.inicioReparo}
								/>
							</label>

							<label className="block text-sm font-semibold">
								<span className="mb-2 block">
									{t('fluxo_unico.resolution.repair_finish')}
								</span>
								<input
									className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
									name="fimReparo"
									onChange={aoAlterarCampo}
									type="datetime-local"
									value={formularioResolucao.fimReparo}
								/>
							</label>
						</div>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">{t('fluxo_unico.resolution.parts_replaced')}</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="pecasTrocadas"
								onChange={aoAlterarCampo}
								placeholder={t('fluxo_unico.resolution.parts_placeholder')}
								value={formularioResolucao.pecasTrocadas}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">{t('fluxo_unico.resolution.execution_time_hours')}</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								min="0"
								name="tempoExecucaoHoras"
								onChange={aoAlterarCampo}
								placeholder="Ex: 2"
								type="number"
								value={formularioResolucao.tempoExecucaoHoras}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								<span className="text-[#dc2626]">*</span> {t('fluxo_unico.resolution.execution_time_minutes')}
							</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								max="59"
								min="0"
								name="tempoExecucaoMinutos"
								onChange={aoAlterarCampo}
								placeholder="Ex: 30"
								required
								type="number"
								value={formularioResolucao.tempoExecucaoMinutos}
							/>
						</label>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">{t('fluxo_unico.resolution.comments')}</span>
							<textarea
								className="min-h-24 w-full rounded-md border border-[#cbd5e1] bg-white p-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="observacaoServico"
								onChange={aoAlterarCampo}
								placeholder={t('fluxo_unico.resolution.comments_placeholder')}
								value={formularioResolucao.observacaoServico}
							/>
						</label>
					</div>
				</div>

				<div className="mt-6 rounded-lg border border-[#d9e1ea] bg-white p-7 shadow-sm">
					<h2 className="mb-7 font-semibold">✍ {t('fluxo_unico.resolution.digital_signature')}</h2>
					<div className="space-y-5">
						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								<span className="text-[#dc2626]">*</span> {t('fluxo_unico.resolution.technician_full_name')}
							</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="nomeCompletoTecnico"
								onChange={aoAlterarCampo}
								required
								type="text"
								value={formularioResolucao.nomeCompletoTecnico}
							/>
						</label>

						<CampoAssinaturaDigital
							aoAlterarAssinatura={aoAlterarAssinaturaTecnico}
							obrigatoria
							titulo={t('fluxo_unico.resolution.technician_signature')}
							valorAssinatura={formularioResolucao.assinaturaTecnico}
						/>

						<label className="block text-sm font-semibold">
							<span className="mb-2 block">
								{t('fluxo_unico.resolution.manager_full_name')}
							</span>
							<input
								className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-[#1f2937] outline-none focus:border-[#2f70f0] focus:ring-2 focus:ring-[#dbeafe]"
								name="nomeCompletoGestor"
								onChange={aoAlterarCampo}
								type="text"
								value={formularioResolucao.nomeCompletoGestor}
							/>
						</label>
						<CampoAssinaturaDigital
							aoAlterarAssinatura={aoAlterarAssinaturaGestor}
							titulo={t('fluxo_unico.resolution.manager_signature')}
							valorAssinatura={formularioResolucao.assinaturaGestor}
						/>
						<p className="text-xs text-[#64748b]">
							{t('fluxo_unico.resolution.manager_signature_notice')}
						</p>
					</div>
				</div>

				{erroResolucaoOrdemServico && (
					<p className="mt-5 rounded-md border border-[#ef4444] bg-[#fff1f2] px-4 py-3 text-sm font-semibold text-[#dc2626]">
						{erroResolucaoOrdemServico}
					</p>
				)}

				<div className="mt-7 flex flex-wrap gap-4 border-t border-[#e2e8f0] pt-7">
					<button
						className="rounded-md border border-[#cbd5e1] bg-white px-5 py-3 font-semibold text-[#475569] transition-colors hover:border-[#94a3b8] hover:bg-[#f8fafc]"
						onClick={aoVoltar}
						type="button"
					>
						{t('fluxo_unico.form.cancel')}
					</button>
					<button
						className="rounded-md border border-[#2f70f0] bg-[#2f70f0] px-5 py-3 text-white transition-colors hover:bg-[#245dcc] disabled:cursor-not-allowed disabled:opacity-70"
						disabled={carregandoResolucaoOrdemServico}
						onClick={() =>
							aoSalvarResolucao('rascunho')
						}
						type="button"
					>
						{carregandoResolucaoOrdemServico
							? t('fluxo_unico.resolution.saving')
							: t('fluxo_unico.resolution.save')}
					</button>
					<button
						className="rounded-md border border-[#16a34a] bg-[#16a34a] px-5 py-3 font-semibold text-white transition-colors hover:bg-[#15803d] disabled:cursor-not-allowed disabled:opacity-70"
						disabled={carregandoResolucaoOrdemServico}
						type="submit"
					>
						{carregandoResolucaoOrdemServico
							? t('fluxo_unico.resolution.sending')
							: t('fluxo_unico.resolution.close_send_email')}
					</button>
				</div>
			</form>
		</section>
	);
};

export default FormularioResolucaoOrdemServico;
